import React, { useState, useRef, useEffect } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { EmailData } from '../../../types/results.types';
import { emailVerificationApi } from '../../../api/emailVerification';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { fetchResults, patchEmailResults } from '../../../store/slices/resultsSlice';
import { isVerifiable } from '../../../utils/emailVerifiable';
import { emailDataSourceToTable, resolveVerificationEmailType } from '../../../utils/verificationEmailType';

interface SingleEmailVerifyButtonProps {
  email: EmailData;
  searchId: number;
}

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_DURATION_MS = 45_000;
const TERMINAL_STATUSES = new Set(['completed', 'cancelled', 'failed']);

const SingleEmailVerifyButton: React.FC<SingleEmailVerifyButtonProps> = ({
  email,
  searchId,
}) => {
  const dispatch = useAppDispatch();
  const activeVerification = useAppSelector((state) => state.emailVerification.activeVerification);
  const places = useAppSelector((state) => state.results.places);
  const filters = useAppSelector((state) => state.results.filters);
  const pagination = useAppSelector((state) => state.results.pagination);

  const [isVerifying, setIsVerifying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const errorResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const emailTable = emailDataSourceToTable(email.source);

  const liveEmail = React.useMemo(() => {
    for (const place of places) {
      const companyEmail = place.company_emails?.find((item) => item.id === email.id);
      if (companyEmail) {
        return companyEmail;
      }
      for (const person of place.people ?? []) {
        const personEmail = person.emails?.find((item) => item.id === email.id);
        if (personEmail) {
          return personEmail;
        }
      }
    }
    return email;
  }, [places, email]);

  useEffect(() => {
    return () => {
      if (errorResetRef.current) {
        clearTimeout(errorResetRef.current);
      }
    };
  }, []);

  const isBlockedByBulkVerification = activeVerification?.status === 'processing';

  const applyVerificationResults = (
    results: Array<{ email_id: number; email_table?: string; email_type?: string; status: string }>,
  ) => {
    const verifiedAt = new Date().toISOString();
    for (const result of results) {
      if (!result.email_id || !result.status) {
        continue;
      }
      dispatch(
        patchEmailResults({
          email_id: result.email_id,
          email_type: resolveVerificationEmailType(result, emailTable),
          status: result.status,
          verified_at: verifiedAt,
        }),
      );
    }
  };

  const refreshResults = () => {
    dispatch(fetchResults({
      searchId,
      page: pagination.page,
      limit: pagination.itemsPerPage,
      filters,
      silent: true,
    }));
  };

  const waitForVerificationCompletion = async (requestId: number) => {
    const startedAt = Date.now();

    while (Date.now() - startedAt < MAX_POLL_DURATION_MS) {
      const status = await emailVerificationApi.getStatus(requestId);

      if (TERMINAL_STATUSES.has(status.status)) {
        if (status.results?.length) {
          applyVerificationResults(status.results);
        }
        refreshResults();
        if (status.status !== 'completed') {
          setErrorMessage('Verification did not complete. Please try again.');
        }
        return;
      }

      await new Promise((resolve) => {
        window.setTimeout(resolve, POLL_INTERVAL_MS);
      });
    }

    setErrorMessage('Still verifying. Results will appear shortly.');
  };

  const handleVerify = async () => {
    if (!email.id || isVerifying || isBlockedByBulkVerification) {
      return;
    }

    setIsVerifying(true);
    setErrorMessage(null);

    try {
      const response = await emailVerificationApi.verify({
        person_email_ids: emailTable === 'person' ? [email.id] : [],
        place_email_ids: emailTable === 'place' ? [email.id] : [],
        email_type: emailTable,
      });

      if (response.processing_mode === 'sync' && response.results?.length) {
        applyVerificationResults(response.results);
        refreshResults();
        return;
      }

      if ('request_id' in response) {
        await waitForVerificationCompletion(response.request_id);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Verification failed';
      const apiMessage = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setErrorMessage(apiMessage || message);
      errorResetRef.current = setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setIsVerifying(false);
    }
  };

  if (!isVerifiable(liveEmail)) {
    return null;
  }

  if (isVerifying || isBlockedByBulkVerification) {
    return (
      <button
        type="button"
        disabled
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-gray-300 text-gray-400 bg-white text-sm font-semibold cursor-not-allowed"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Verifying...
      </button>
    );
  }

  if (errorMessage) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleVerify();
        }}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-red-400 text-red-600 bg-white text-sm font-semibold hover:bg-red-50 transition-all duration-200"
        title={errorMessage}
      >
        <Mail className="w-4 h-4" />
        Retry
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        handleVerify();
      }}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-[#8A3033] text-[#8A3033] bg-white text-sm font-semibold hover:bg-[#8A3033] hover:text-white transition-all duration-200"
      title="Verify this email"
    >
      <Mail className="w-4 h-4" />
      Verify Email
    </button>
  );
};

export default SingleEmailVerifyButton;
